import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { RESET_PASSWORD_WITH_TOKEN_MUTATION } from "@/graphql/auth";

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(6, "auth:resetPassword.errors.passwordTooShort"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "auth:resetPassword.errors.passwordMismatch",
    path: ["confirmPassword"],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

interface UseResetPasswordFormOptions {
  token: string;
  onSuccess?: () => void;
}

export function useResetPasswordForm({ token, onSuccess }: UseResetPasswordFormOptions) {
  const [resetPasswordWithToken, { loading }] = useMutation(RESET_PASSWORD_WITH_TOKEN_MUTATION);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleSubmit = async (data: ResetPasswordFormData) => {
    try {
      await resetPasswordWithToken({
        variables: {
          input: {
            token,
            newPassword: data.newPassword,
          },
        },
      });

      onSuccess?.();
    } catch (error: any) {
      console.error("Reset password error:", error);
      toast.error(error.message || "Failed to reset password. Please try again.");
    }
  };

  return {
    form,
    isLoading: loading,
    onSubmit: form.handleSubmit(handleSubmit),
  };
}
